/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ENV } from '@app/constants/global.constants';
import { IMAGE_QUALITY_LIST } from '@app/constants/imatgeQuality';
import { ViewTypesConstants, isClinicGuideView, isDirectContentView } from '@app/constants/viewTypes.constants';
import { AuthenticationService } from '@app/security/authentication.service';
import { Env } from '@app/types/env';
import { SemDB } from '@data/indexedDB/sem.db';
import PaginationResponseI from '@data/interfaces/pagination-response';
import ContentDTO from '@data/models/content/content-dto';
import { url } from 'inspector';
import { BehaviorSubject, Subject } from 'rxjs';
import { take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class DbProgressiveDownloadService {
  public readonly PROGRESS_FETCHING_DATA_SIZE = 40;
  public readonly FIRST_LOAD_COMPLETED_KEY = 'first_load_completed';
  public progressSubject: BehaviorSubject<number> = new BehaviorSubject(100);
  public totalElementsInActualProgress = 0;
  public semDB: SemDB;
  public updatingContentInformation: Subject<{
    view: ViewTypesConstants;
    updates: {
      deleting: number;
      updating: number;
      adding: number;
    };
  }> = new Subject();
  public storageStatus: { setted: boolean; quota: number; usage: number; percentageUsed: string } = {
    setted: false,
    quota: 0,
    usage: 0,
    percentageUsed: ''
  };
  private tables: ViewTypesConstants[] = Object.values(ViewTypesConstants);
  private dbTablesState: {
    [key: string]: {
      initialized: boolean;
      contentSubject: Subject<ContentDTO[]>;
      fetchingData: boolean;
      totalLength: number;
      withoutData?: boolean;
      actualDataLength: number;
    };
  } = {};
  private wWidth: number;

  private isMobile: boolean;

  private readonly API_PATH = '/api';
  private readonly GUIES_CLINIQUES_PATH = '/guies-cliniques';
  private readonly CODIS_ACTIVACIO_PATH = '/codis-activacio';
  private readonly REPOSITORI_PATH = '/repositori';
  private readonly FILTER_PATH = '/filtrar';

  constructor(@Inject(ENV) private env: Env, private http: HttpClient, private authService: AuthenticationService) {
    this.semDB = new SemDB();
    this.wWidth = window.innerWidth;
    this.isMobile = /iPhone|iPad|iPod|Android/i.test(window.navigator.userAgent);
  }

  public async checkStorageQuota(): Promise<void> {
    if (navigator.storage && navigator.storage.estimate) {
      const quota = await navigator.storage.estimate();
      // quota.usage -> Number of bytes used.
      // quota.quota -> Maximum number of bytes available.
      const percentageUsed = ((quota.usage / quota.quota) * 100).toFixed(2);
      if (!this.storageStatus.setted) {
        console.log(`You've used ${percentageUsed}% of the available storage.`);
      }
      this.storageStatus.setted = true;
      this.storageStatus.quota = quota.quota;
      this.storageStatus.usage = quota.usage;
      this.storageStatus.percentageUsed = percentageUsed;
    }
  }

  public checkDB(): void {
    let tablesSetted = 0;
    this.tables.forEach((k) => {
      this.dbTablesState[k] = {
        initialized: false,
        contentSubject: new Subject(),
        fetchingData: false,
        totalLength: 0,
        actualDataLength: 0
      };
    });
    this.tables.forEach((k) => {
      this.semDB[k].count().then((n: number) => {
        tablesSetted += 1;
        if (!n || n <= 0) {
          //Si no tiene datos la inicialización se setea a false
          this.dbTablesState[k].initialized = false;
        } else {
          this.dbTablesState[k].initialized = true;
        }
        if (tablesSetted === this.tables.length) {
          this.checkDbInitialized();
        }
      });
    });
  }

  public getContentTableSubject(view: ViewTypesConstants): Subject<ContentDTO[]> {
    return this.dbTablesState[view].contentSubject;
  }

  public isDownlodingDataForTable(view: ViewTypesConstants): boolean {
    return this.dbTablesState[view]?.fetchingData;
  }

  public async fetchData(view?: ViewTypesConstants): Promise<void> {
    if (!view && this.isMobile) {
      // Si es mobile, cargar todas las vistas a la vez
      this.resetProgress();
      this.addWindowBeforeUnloadEvent();
      const fetchPromises = this.tables.map(async (k) => {
        if (this.dbTablesState[k] && !this.dbTablesState[k].initialized && !this.dbTablesState[k].fetchingData) {
          if (k !== ViewTypesConstants.repositori || this.isAdministradorOrResponsable()) {
            this.dbTablesState[k].fetchingData = true;
            await this.fetchDataByViewPaginatedAsync(k, 0);
          }
        }
      });
      await Promise.all(fetchPromises);
    } else if (view && this.dbTablesState[view] && !this.dbTablesState[view].fetchingData) {
      // Si es web y hay una vista específica, primero cargar INT y mostrarlo de inmediato
      this.addWindowBeforeUnloadEvent();
      this.resetProgress(view);

      const initialViews: ViewTypesConstants[] = [
        ViewTypesConstants.INT,  // Descarga y muestra primero INT
        ViewTypesConstants.SVA,
        ViewTypesConstants.SVB,
        ViewTypesConstants.CECOS
      ];

      // Descarga INT primero y espera a que se complete
      if (this.dbTablesState[ViewTypesConstants.INT] && !this.dbTablesState[ViewTypesConstants.INT].initialized) {
        this.dbTablesState[ViewTypesConstants.INT].fetchingData = true;
        await this.fetchDataByViewPaginatedAsync(ViewTypesConstants.INT, 0);
      }

      // Luego sigue con las demás vistas en paralelo sin bloquear
      const fetchOtherViews = initialViews.slice(1).map(async (k) => {
        if (this.dbTablesState[k] && !this.dbTablesState[k].initialized) {
          this.dbTablesState[k].fetchingData = true;
          await this.fetchDataByViewPaginatedAsync(k, 0);
        }
      });

      Promise.all(fetchOtherViews).catch(error => console.error('Error en la carga de vistas:', error));

      // Finalmente, descargar la vista específica si es diferente de INT
      if (view !== ViewTypesConstants.INT) {
        this.dbTablesState[view].fetchingData = true;
        await this.fetchDataByViewPaginatedAsync(view, 0);
      }
    }
  }

  public getRequestParamQuality(): 'FHD' | 'HD' | 'SD' {
    if (!this.isAdministradorOrResponsable()) {
      if (this.wWidth < IMAGE_QUALITY_LIST.SD) {
        return 'SD';
      } else if (this.wWidth < IMAGE_QUALITY_LIST.HD) {
        return 'HD';
      }
    }
    return 'FHD';
  }

  public isAdministradorOrResponsable(): boolean {
    const userRole = this.authService.getUserRole();
    return userRole === 'ADMINISTRADOR' || userRole === 'RESPONSABLE';
  }

  private async fetchDataByViewPaginatedAsync(view: ViewTypesConstants, page: number): Promise<void> {
    let viewPath = `/${view}`;
    let filter: { tipus: ViewTypesConstants[] } = null;

    if (isClinicGuideView(view) || isDirectContentView(view)) {
      viewPath = this.GUIES_CLINIQUES_PATH;
      filter = { tipus: [view] };
    } else if (view === ViewTypesConstants.codisActivacio) {
      viewPath = this.CODIS_ACTIVACIO_PATH;
    } else if (view === ViewTypesConstants.repositori) {
      viewPath = this.REPOSITORI_PATH;
    }
    // eslint-disable-next-line max-len
    let urlToUse = `${this.env.apiBaseUrl}${this.API_PATH}${viewPath}${this.FILTER_PATH}?page=${page}&size=${this.PROGRESS_FETCHING_DATA_SIZE}&paginarResultados=true`;

    if (isDirectContentView(view)) {
      urlToUse += `&resolucioImatges=${this.getRequestParamQuality()}`;
    }

    let data = await this.http.post<PaginationResponseI<ContentDTO>>(urlToUse, filter).toPromise();

    if (!data || !data.content) {
      data = {
        pageable: null,
        totalElements: 0,
        withoutData: true,
        totalPages: 0,
        last: true,
        numberOfElements: 0,
        first: true,
        sort: null,
        size: 0,
        // eslint-disable-next-line id-blacklist
        number: 0,
        content: [],
      };
    }

    for (const content of data.content) {
      //Se guarda en IndexedDB
      await this.semDB[view].put(content);
    }

    this.dbTablesState[view].actualDataLength += data.numberOfElements;
    this.dbTablesState[view].totalLength = data.totalElements;
    this.dbTablesState[view].withoutData = data.withoutData;
    this.dbTablesState[view].contentSubject.next(data.content);
    this.recalculateProgress();

    if (!data.last) {
      await this.fetchDataByViewPaginatedAsync(view, page + 1);
    } else {
      this.dbTablesState[view].fetchingData = false;
      this.dbTablesState[view].initialized = true;
    }
  }

  private checkDbInitialized(): void {
    const sameLength = Object.keys(this.dbTablesState).length === this.tables.length;
    const firstLoadCompleted = localStorage.getItem(this.FIRST_LOAD_COMPLETED_KEY) === 'true';
    const allTablesInitialized = Object.keys(this.dbTablesState).reduce((a: boolean, b: string) => {
      if (!this.dbTablesState[b].initialized) {
        return false;
      }
      return a;
    }, true);

    if (sameLength && !allTablesInitialized && this.isMobile && !firstLoadCompleted) {
      this.progressSubject.next(0);
      this.fetchData();
    }
  }

  private recalculateProgress(): void {
    let totalItems = 0;
    let actualItems = 0;
    let allTotalsForFetchingTables = true;
    let allTablesFetched = true;
    let intFetched = false;
    this.tables.forEach((k) => {
      const table = this.dbTablesState[k];
      if (table?.fetchingData) {
        if (!table.totalLength && !table.withoutData) {
          allTotalsForFetchingTables = false;
        }
        if (table.totalLength !== table.actualDataLength || (!table.totalLength && !table.withoutData)) {
          allTablesFetched = false;
        }
        totalItems += table.totalLength;
        actualItems += table.actualDataLength;
      }
      // Verificar si la vista es INT
      if (k === ViewTypesConstants.INT) {
        intFetched = table?.initialized || false;
      }
    });
    if (allTablesFetched) {
      this.tables.forEach((k) => {
        const table = this.dbTablesState[k];
        if (table?.fetchingData) {
          table.actualDataLength = 0;
          table.fetchingData = false;
          table.initialized = true;
          table.totalLength = 0;
        }
      });
      localStorage.setItem(this.FIRST_LOAD_COMPLETED_KEY, 'true');
      this.checkStorageQuota();
      this.removeWindowBeforeUnloadEvent();
    }
    if (allTotalsForFetchingTables) {
      // Si es INT y aún no está completamente descargado, no poner el progreso al 100%
      if (intFetched) {
        if (actualItems === 0 && totalItems === 0) {
          this.progressSubject.next(100);
        } else {
          this.progressSubject.next(Math.floor((actualItems * 100) / totalItems));
        }
      } else {
        this.progressSubject.next(0); // Mientras INT no esté descargado, progreso en 0%
      }
    }
  }

  private resetProgress(view?: ViewTypesConstants): void {
    if (!view) {
      this.tables.forEach((k) => {
        this.dbTablesState[k].actualDataLength = 0;
        this.dbTablesState[k].totalLength = 0;
      });
    } else {
      this.dbTablesState[view].actualDataLength = 0;
      this.dbTablesState[view].totalLength = 0;
    }
    this.progressSubject.next(0);
  }

  private addWindowBeforeUnloadEvent(): void {
    window.addEventListener('beforeunload', this.beforeUnloadEvent);
  }

  private removeWindowBeforeUnloadEvent(): void {
    window.removeEventListener('beforeunload', this.beforeUnloadEvent);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private beforeUnloadEvent = (event: any) => {
    event.returnValue = `¿Está seguro que desea salir?`;
  };
}
